import { ACCOUNT_TYPES, ROLES, type Account, type AccountRelationship, type AccountType, type Address, type RelationshipType, type Role, type User } from './types'
import type { AccountDto, AccountRelationshipDto, UserDto } from '@/api'

/** The backend's role names. ADMIN is a role there; here it is the `isAccountAdmin` flag next to one working role. */
export const ADMIN_ROLE = 'ADMIN'

const clean = (value: string | null | undefined) => (value ?? '').trim()
const isAccountType = (value: string): value is AccountType => (ACCOUNT_TYPES as readonly string[]).includes(value)

/** Null for an account type this app does not know, so one odd record never breaks the whole list. */
export function toAccount(api: AccountDto): Account | null {
  if (!isAccountType(api.accountType)) return null
  const address: Address = { line1: clean(api.addressLine1), line2: '', city: clean(api.city), region: clean(api.state), postalCode: clean(api.postalCode), country: clean(api.country) }
  return {
    id: api.id,
    accountName: api.accountName,
    accountType: api.accountType,
    logoUrl: clean(api.logoUrl) || null,
    createdAt: api.createdAt,
    email: clean(api.email),
    phoneNumber: clean(api.phoneNumber),
    address,
    isActive: api.isActive,
  }
}

/** Which working role stands for a person where only one is shown. */
const PRIMARY_ORDER: readonly Role[] = ['DESIGNER', 'EDITOR', 'APPROVER', 'REVIEWER']

/**
 * ADMIN becomes the admin flag; every other role the app knows is a working role. An admin with no working role (the
 * account's first user) works as a designer, as the first admin always did.
 */
export function toUser(api: UserDto): User {
  const names = api.roles.map((role) => role.toUpperCase())
  const held = ROLES.filter((role) => names.includes(role))
  const roles: Role[] = held.length ? [...held] : ['DESIGNER']
  return {
    id: api.id,
    accountId: api.accountId,
    firstName: api.firstName,
    lastName: api.lastName,
    email: api.email,
    role: PRIMARY_ORDER.find((role) => roles.includes(role)) ?? 'DESIGNER',
    roles,
    isAccountAdmin: names.includes(ADMIN_ROLE),
    createdAt: api.createdAt,
    phoneNumber: clean(api.phoneNumber),
    isActive: api.isActive,
  }
}

/** The role list to send: the working roles, plus ADMIN for an account admin. */
export const rolesFor = (roles: readonly Role[], isAccountAdmin: boolean): string[] => (isAccountAdmin ? [ADMIN_ROLE, ...roles] : [...roles])

/**
 * What kind of link a parent and child account make. Only three are valid: SupplyLogic above a supplier, SupplyLogic above a
 * client it manages directly, and a supplier above one of its clients. Anything else is null.
 */
export function relationshipTypeOf(parent: AccountType, child: AccountType): RelationshipType | null {
  if (parent === 'SUPPLY_LOGIC' && child === 'SUPPLIER') return 'SUPPLY_LOGIC_SUPPLIER'
  if (parent === 'SUPPLY_LOGIC' && child === 'CLIENT') return 'SUPPLY_LOGIC_CLIENT'
  if (parent === 'SUPPLIER' && child === 'CLIENT') return 'SUPPLIER_CLIENT'
  return null
}

/** An active link between two known accounts, typed from their account types (not from the backend's own label). Null otherwise. */
export function toRelationship(api: AccountRelationshipDto, typeOf: (accountId: string) => AccountType | undefined): AccountRelationship | null {
  if (!api.isActive) return null
  const parent = typeOf(api.parentAccountId)
  const child = typeOf(api.childAccountId)
  const relationshipType = parent && child ? relationshipTypeOf(parent, child) : null
  return relationshipType ? { id: api.id, parentAccountId: api.parentAccountId, childAccountId: api.childAccountId, relationshipType, createdAt: api.createdAt } : null
}

/** The three lists the backend gave, as the directory the rest of the app reads. A link between accounts the app does not know is left out. */
export function toDirectory(dto: { accounts: readonly AccountDto[]; users: readonly UserDto[]; relationships: readonly AccountRelationshipDto[] }) {
  const accounts = dto.accounts.flatMap((item) => toAccount(item) ?? [])
  const types = new Map(accounts.map((account) => [account.id, account.accountType]))
  return {
    accounts,
    users: dto.users.map(toUser),
    relationships: dto.relationships.flatMap((link) => toRelationship(link, (id) => types.get(id)) ?? []),
  }
}
